import jwtDefaultConfig from "./jwtDefaultConfig";

/**
 * Thin wrapper around sessionStorage so we can swap it out in one place.
 *
 * WHY sessionStorage instead of localStorage?
 *   - localStorage persists indefinitely (survives browser close) → XSS can
 *     steal the token long after the user has walked away from the desk.
 *   - sessionStorage is cleared when the tab/browser closes → much smaller
 *     attack window while still surviving page refreshes within the session.
 *
 * NEXT STEP (when time allows): migrate to HttpOnly cookie via Sanctum SPA
 * authentication so the token is completely invisible to JavaScript.
 */
const storage = {
  getItem: (key) => sessionStorage.getItem(key),
  setItem: (key, val) => sessionStorage.setItem(key, val),
  removeItem: (key) => sessionStorage.removeItem(key),
};

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null;

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig };

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false;

  // For Refreshing Token
  subscribers = [];

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns;
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig };

    // ── Request Interceptor ────────────────────────────────────────────────────
    this.axiosIns.interceptors.request.use(
      (config) => {
        const accessToken = this.getToken();
        if (accessToken) {
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // ── Response Interceptor ───────────────────────────────────────────────────
    this.axiosIns.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;

        if (response && response.status === 401) {
          // Token expired or revoked — clear session and redirect to login
          this._clearSession();
          // Use window.location so the redirect works even outside a Vue component
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /** Remove all session data stored by this service. */
  _clearSession() {
    storage.removeItem(this.jwtConfig.storageTokenKeyName);
    storage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
    storage.removeItem("userData");
    storage.removeItem("hatchery_id");
    storage.removeItem("role");
    storage.removeItem("company_id");
    // Also clear any lingering localStorage entries from old builds
    try {
      localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
      localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
      localStorage.removeItem("userData");
      localStorage.removeItem("accessToken");
    } catch (_) {}
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter((callback) =>
      callback(accessToken)
    );
  }

  addSubscriber(callback) {
    this.subscribers.push(callback);
  }

  getToken() {
    return storage.getItem(this.jwtConfig.storageTokenKeyName);
  }

  getRefreshToken() {
    return storage.getItem(this.jwtConfig.storageRefreshTokenKeyName);
  }

  setToken(value) {
    storage.setItem(this.jwtConfig.storageTokenKeyName, value);
    // Clean up any leftover localStorage token from previous builds
    try { localStorage.removeItem(this.jwtConfig.storageTokenKeyName); } catch (_) {}
  }

  setRefreshToken(value) {
    storage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value);
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args);
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args);
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    });
  }
}
